import { Box, ButtonBase, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CONTROL_HEIGHT, FONT_SIZE, INK, NEUTRAL } from "@/theme/tokens";
import { FilterFieldRow } from "@/ui/Filters/FilterFieldRow";
import { toIsoDate } from "@/utils/scheduleWeeks";
import { GapsWeekStrip } from "../GapsWeekStrip";
import { GAPS_DAY_LABEL_FORMAT } from "../gapsLabels";
import { useGapsCountsHidden } from "../useGapsCountsHidden";
import { useGapsVisibleWeek } from "../useGapsVisibleWeek";

type Props = {
	/** The day the page acts on — the home's day, which a pick moves. */
	date: DateTime;
	/**
	 * The home's week shortfalls (`GapsHomeModel.weekShortfalls`), or `undefined` while they are not final. Drawn on the
	 * picker's week only while it is the home's week — the only week whose numbers exist.
	 */
	homeWeekShortfalls: readonly number[] | undefined;
	/** Moves the page, and the home under it, to the picked day. `undefined` = the day is set by what the page acts on. */
	onPick: ((date: DateTime) => void) | undefined;
	/** The day can be changed but not right now (a post on its way): the row keeps its tag and only stops opening. */
	disabled?: boolean;
	/**
	 * Past days can be picked too, back to the schedule's first week: Log a call-in records a call-off after the fact, as it
	 * always could on a past day. Absent, the days that are over are shown but not offered (Post, Add).
	 */
	allowsPast?: boolean;
	/** `{prefix}-date`, `{prefix}-date-picker-…`. */
	testIdPrefix: string;
};

/**
 * The Date row that leads Post a shift and Add someone (APP-886, the design's `postPage` / `addPage`).
 *
 * Closed, it is the day and what it is to the user — today, tomorrow, or another day to change. Open, it is the home's
 * own week strip (the same day cells and −N / ✓ marks), paging through the schedule's weeks, with the days that are over
 * shown but not offered: nothing is posted or added for the past. A pick moves the page and the home under it to that
 * day, so everything the page shows from then on is that day's.
 *
 * Without `onPick` the row only states the day: the page was opened for a hole, a shift or a request on that day.
 */
// DEFAULT: `disabled` absent = a changeable day is changeable now; only Post's in-flight write pauses it. `allowsPast`
// absent = nothing is posted or added for a day that is over (APP-886).
export const GapsDateField: FC<Props> = ({ date, homeWeekShortfalls, onPick, disabled = false, allowsPast = false, testIdPrefix }) => {
	const { t } = useTranslation();
	const { weekStartOf, daysOfWeek, firstDay, lastDay } = useGapsVisibleWeek();
	const { areCountsHidden } = useGapsCountsHidden();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const homeWeekStart = weekStartOf(date);
	const [pickerWeekStart, setPickerWeekStart] = useState<DateTime>(homeWeekStart);

	const today = DateTime.now().startOf("day");
	const todayWeekStart = weekStartOf(today);
	// No week before the first one offered (today's, or the schedule's first), and none past the schedule.
	const firstWeekStart = firstDay !== undefined && (allowsPast || toIsoDate(firstDay) > toIsoDate(todayWeekStart)) ? firstDay : todayWeekStart;
	const canGoBack = firstDay !== undefined && toIsoDate(pickerWeekStart) > toIsoDate(firstWeekStart);
	const canGoForward = lastDay !== undefined && toIsoDate(pickerWeekStart) < toIsoDate(weekStartOf(lastDay));
	const days = useMemo<DateTime[]>(() => daysOfWeek(pickerWeekStart), [daysOfWeek, pickerWeekStart]);

	const dayOffset = Math.round(date.startOf("day").diff(today, "days").days);
	const tag =
		onPick === undefined ? null : dayOffset === 0 ? t("gaps.dateField.today") : dayOffset === 1 ? t("gaps.dateField.tomorrow") : t("gaps.dateField.change");

	const handleToggle = (): void => {
		// Every opening starts on the page's own week, wherever an earlier look paged to.
		setPickerWeekStart(homeWeekStart);
		setIsOpen((wasOpen) => !wasOpen);
	};

	return (
		<FilterFieldRow label={t("gaps.dateField.label")} dataTestId={`${testIdPrefix}-date`}>
			<ButtonBase
				data-testid={`${testIdPrefix}-date-value`}
				disabled={onPick === undefined || disabled}
				aria-expanded={onPick === undefined ? undefined : isOpen}
				onClick={handleToggle}
				sx={{
					width: "100%",
					minHeight: CONTROL_HEIGHT.STANDARD,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 1,
					px: 1.5,
					border: `1px solid ${NEUTRAL[300]}`,
					borderRadius: 1,
					textAlign: "left",
				}}
			>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY }}>
					{date.toFormat(GAPS_DAY_LABEL_FORMAT)}
				</Typography>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
					{tag === null ? t("gaps.dateField.locked") : `${tag} ›`}
				</Typography>
			</ButtonBase>
			{isOpen && onPick !== undefined && !disabled && (
				<Box sx={{ mt: 1, border: `1px solid ${NEUTRAL[200]}`, borderRadius: 1, overflow: "hidden" }}>
					<GapsWeekStrip
						days={days}
						shortfalls={toIsoDate(pickerWeekStart) === toIsoDate(homeWeekStart) && !areCountsHidden ? homeWeekShortfalls : undefined}
						selectedDate={date}
						onSelectDate={(picked) => {
							setIsOpen(false);
							if (picked.toISODate() !== date.toISODate()) onPick(picked);
						}}
						onPreviousWeek={() => {
							if (canGoBack) setPickerWeekStart(pickerWeekStart.minus({ weeks: 1 }));
						}}
						onNextWeek={() => {
							if (canGoForward) setPickerWeekStart(pickerWeekStart.plus({ weeks: 1 }));
						}}
						canGoBack={canGoBack}
						canGoForward={canGoForward}
						isDayDisabled={allowsPast ? undefined : (day) => day < today}
						testIdPrefix={`${testIdPrefix}-date-picker`}
					/>
				</Box>
			)}
		</FilterFieldRow>
	);
};
